import { useState, useCallback, useRef } from 'react';
import {
    EMPTY, VALID_MOVE, BOARD_SIZE, TOTAL_PLACEMENTS,
    PHASE_PLACEMENT, PHASE_MOVEMENT, PHASE_GAME_OVER,
} from '../gameConstants';
import { getValidMovesForPiece, hasAnyValidMove, checkForWinner } from '../utils/directions';

function createEmptyBoard() {
    return Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(EMPTY));
}

function clearValidMoves(board) {
    return board.map(row => row.map(cell => (cell === VALID_MOVE ? EMPTY : cell)));
}

export default function useGame() {
    const [board, setBoard] = useState(createEmptyBoard);
    const [gamePhase, setGamePhase] = useState(PHASE_PLACEMENT);
    const [cnt, setCnt] = useState(0);
    const [rolledNumber, setRolledNumber] = useState(0);
    const [pieceSelected, setPieceSelected] = useState(null);
    const [hasValidMoves, setHasValidMoves] = useState(true);
    const [log, setLog] = useState("");
    const [winningCells, setWinningCells] = useState([]);
    const soundRefs = useRef({ stomp: null, roll: null });

    const currentPlayer = cnt % 3;
    const isPlacementPhase = gamePhase === PHASE_PLACEMENT;
    const isMovementPhase = gamePhase === PHASE_MOVEMENT;
    const isGameOver = gamePhase === PHASE_GAME_OVER;
    const placementProgress = Math.min(cnt, TOTAL_PLACEMENTS);

    const playSound = useCallback((type) => {
        const audio = soundRefs.current[type];
        if (!audio) return;
        try {
            if (audio.paused) {
                audio.play();
            } else {
                audio.currentTime = 0;
            }
        } catch {
            // Audio blocked by browser
        }
    }, []);

    const registerSound = useCallback((type, element) => {
        if (element) soundRefs.current[type] = element;
    }, []);

    const placePiece = useCallback((row, col) => {
        if (gamePhase !== PHASE_PLACEMENT) return;
        if (board[row][col] !== EMPTY) return;

        const newBoard = board.map(r => [...r]);
        newBoard[row][col] = currentPlayer + 1;
        setBoard(newBoard);
        playSound('stomp');

        const nextCnt = cnt + 1;
        setCnt(nextCnt);

        if (nextCnt >= TOTAL_PLACEMENTS) {
            setGamePhase(PHASE_MOVEMENT);
        }

        const result = checkForWinner(newBoard);
        if (result) {
            setWinningCells(result.cells);
            setGamePhase(PHASE_GAME_OVER);
        }
    }, [gamePhase, board, currentPlayer, cnt, playSound]);

    const roll = useCallback(() => {
        if (gamePhase !== PHASE_MOVEMENT || rolledNumber !== 0) return;

        playSound('roll');
        const randomNumber = Math.floor(Math.random() * 6) + 1;
        setRolledNumber(randomNumber);

        const valid = hasAnyValidMove(board, currentPlayer + 1, randomNumber);
        setHasValidMoves(valid);
        setLog(valid ? "" : "NO VALID MOVES AVAILABLE");
    }, [gamePhase, rolledNumber, board, currentPlayer, playSound]);

    const selectPiece = useCallback((row, col) => {
        if (gamePhase !== PHASE_MOVEMENT) return null;
        if (rolledNumber === 0) {
            setLog("ROLL NUMBER FIRST");
            return null;
        }
        if (board[row][col] !== currentPlayer + 1) {
            setLog("SELECT YOUR PIECE");
            return null;
        }

        const moves = getValidMovesForPiece(board, row, col, rolledNumber);
        if (moves.length === 0) {
            setLog("PIECE NOT MOVEABLE");
            return null;
        }

        // Mark valid moves on board
        const newBoard = board.map(r => [...r]);
        for (const m of moves) {
            newBoard[m.row][m.col] = VALID_MOVE;
        }
        setBoard(newBoard);
        setPieceSelected([row, col]);
        setLog("");
        return moves;
    }, [gamePhase, rolledNumber, board, currentPlayer]);

    const deselectPieceAction = useCallback(() => {
        setBoard(prev => clearValidMoves(prev));
        setPieceSelected(null);
        setLog("");
    }, []);

    const movePiece = useCallback((row, col) => {
        if (!pieceSelected) return;
        if (board[row][col] !== VALID_MOVE) {
            setLog("FIELD NOT VALID");
            return;
        }

        const [sr, sc] = pieceSelected;
        const newBoard = clearValidMoves(board);
        newBoard[sr][sc] = EMPTY;
        newBoard[row][col] = currentPlayer + 1;
        setBoard(newBoard);
        playSound('stomp');

        setPieceSelected(null);
        setRolledNumber(0);
        setHasValidMoves(true);
        setLog("");

        const nextCnt = cnt + 1;
        setCnt(nextCnt);

        const result = checkForWinner(newBoard);
        if (result) {
            setWinningCells(result.cells);
            setGamePhase(PHASE_GAME_OVER);
        }
    }, [pieceSelected, board, currentPlayer, cnt, playSound]);

    const skipTurn = useCallback(() => {
        setRolledNumber(0);
        setHasValidMoves(true);
        setPieceSelected(null);
        setBoard(prev => clearValidMoves(prev));
        setLog("");
        setCnt(prev => prev + 1);
    }, []);

    const restart = useCallback(() => {
        setBoard(createEmptyBoard());
        setCnt(0);
        setRolledNumber(0);
        setPieceSelected(null);
        setHasValidMoves(true);
        setLog("");
        setGamePhase(PHASE_PLACEMENT);
        setWinningCells([]);
    }, []);

    return {
        board,
        gamePhase,
        currentPlayer,
        cnt,
        rolledNumber,
        pieceSelected,
        hasValidMoves,
        log,
        setLog,
        winningCells,
        isPlacementPhase,
        isMovementPhase,
        isGameOver,
        placementProgress,
        placePiece,
        roll,
        selectPiece,
        deselectPiece: deselectPieceAction,
        movePiece,
        skipTurn,
        restart,
        registerSound,
    };
}
